import React, { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { FiltersProvider } from '@Sage/ssm-web5';
import { Text, Spacer } from '@Sage/hoi-poi-ui';
import { COMPANIES } from 'constants/Entities';
import { EntityListSelectActions } from 'actions';
import { MarketingStatusCell } from 'components/TableCells';
import FiltersLayout from 'containers/components/layouts/FiltersLayout';
import EntityMenu from 'containers/components/EntityMenu';
import NewEntityList from 'containers/components/NewEntityList';
import SearchCompanies from 'containers/companies/components/SearchCompanies';
import { getIncludedSelectionIds } from 'utils/bulkSelection';
import { getLiteral } from 'utils/getLiteral';
import CONFIG, { FILTER_KEY } from './CompaniesTableConfig';
import useMarketingBulkSubscribeExtraActions from '../bulkSubscribe/useMarketingBulkSubscribeExtraActions';

const CACHE_ID = 'subscribers_companies_list';

function mapStateToProps(state) {
    const selection = state.entityListSelect[COMPANIES.entity];

    return {
        bulkSelectedEntities: selection?.total ?? 0,
        selectedIds: getIncludedSelectionIds(selection),
        shouldClearWeb5Checkboxes: selection?.shouldClearWeb5Checkboxes,
        spamchimp: state?.config?.flagsmith?.spamchimp,
        canEdit: !!state?.config?.permission?.crud_permission?.[COMPANIES.permission]?.update,
    };
}

const mapDispatchToProps = (dispatch) => ({
    onEntitySelect: bindActionCreators(EntityListSelectActions, dispatch).onEntitySelect,
    onEntitySelectAll: bindActionCreators(EntityListSelectActions, dispatch).onEntitySelectAll,
    setShouldClearWeb5Checkboxes: bindActionCreators(EntityListSelectActions, dispatch)
        .setShouldClearWeb5Checkboxes,
});

const CompaniesTab = memo(
    ({
        bulkSelectedEntities,
        selectedIds,
        shouldClearWeb5Checkboxes,
        spamchimp,
        canEdit,
        onEntitySelect,
        onEntitySelectAll,
        setShouldClearWeb5Checkboxes,
        onRequestBulkSubscribedConfirm,
    }) => {
        const tableRef = useRef(null);

        useEffect(() => {
            if (shouldClearWeb5Checkboxes) {
                if (tableRef?.current?.clearCheckboxes) tableRef.current.clearCheckboxes();
                setShouldClearWeb5Checkboxes(COMPANIES, false);
            }
        }, [setShouldClearWeb5Checkboxes, shouldClearWeb5Checkboxes]);

        const handleOnRef = useCallback((ref) => {
            tableRef.current = ref;
        }, []);

        const onClickCheckbox = useCallback(
            ({ id, isColumn, value, checked, data, rowIndex, total }) => {
                if (isColumn) {
                    onEntitySelectAll(COMPANIES, value, checked, total, FILTER_KEY);
                } else {
                    onEntitySelect(COMPANIES, id, value, data, rowIndex, checked);
                }
            },
            [onEntitySelect, onEntitySelectAll],
        );

        const config = useMemo(
            () => CONFIG({ onClickCheckbox, spamchimp, canEdit }),
            [onClickCheckbox, spamchimp, canEdit],
        );

        const bulkOperationsLiterals = useMemo(
            () => ({
                labelTitleMenuTotalSelected: 'label_num_accounts_selected',
                labelTitleMenuTotalSelectedSingular: 'label_num_accounts_selected_singular',
                entityNamePlural: 'label_accounts',
            }),
            [],
        );

        const extraBulkActions = useMarketingBulkSubscribeExtraActions({
            entity: COMPANIES,
            filterKey: config?.filterKey,
            bulkSelectedEntities,
            selectedIds,
            onRequestBulkSubscribedConfirm,
        });

        if (!config) return null;

        return (
            <FiltersProvider>
                <div className="fm-subscribers-tab">
                    <Text type="h6" className="fm-subscribers-tab__title">
                        {getLiteral('label_accounts')}
                    </Text>
                    <Spacer y={2} />
                    <EntityMenu
                        entity={COMPANIES}
                        filterKey={config.filterKey}
                        withFilters={true}
                        quickFilters={config.quickFilters}
                        useQuickView={config.useQuickView}
                        searchComponent={<SearchCompanies filterKey={config.filterKey} />}
                        withBulkOperations={true}
                        bulkSelectedEntities={bulkSelectedEntities}
                        bulkOperationsLiterals={bulkOperationsLiterals}
                        extraBulkActions={extraBulkActions}
                        hideDefaultActions={true}
                        canMassiveUpdate={false}
                        canMassiveDelete={false}
                        canMerge={false}
                    />
                    <FiltersLayout
                        config={config}
                        className="fm-subscribers-tab__list-layout fm-filters-layout--list-side-gap"
                    >
                        <NewEntityList
                            id="fm-grid-subscribers-companies"
                            entity={COMPANIES}
                            config={config}
                            cacheId={CACHE_ID}
                            filterKey={config.filterKey}
                            initOnReady={true}
                            useLazyLoad={true}
                            useCache={true}
                            useSort={true}
                            useSelectColumns={true}
                            useFieldSelector={false}
                            discardCount={true}
                            domLayout="normal"
                            components={{ MarketingStatusCell }}
                            handleOnRef={handleOnRef}
                        />
                    </FiltersLayout>
                </div>
            </FiltersProvider>
        );
    },
);

export default connect(mapStateToProps, mapDispatchToProps)(CompaniesTab);
